import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Wine, AlertTriangle, CheckCircle, Clock, Target, TrendingDown, Heart, CalendarDays, Minus, DollarSign } from 'lucide-react';
import { SparkleGuide } from './guide/SparkleGuide';
import ReductionCounter from './gamification/ReductionCounter';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';

interface AlcoholData {
  units_consumed: number;
  drinks: Array<{
    type: string;
    amount: number;
    abv: number;
    units: number;
  }>;
  risk_level: 'green' | 'amber' | 'red';
  craving_strength?: number;
  craving_resisted?: boolean;
  support_mode?: boolean;
  triggers?: string;
  coping_strategies?: string[];
  delay_used?: number;
}

interface AlcoholTrackerProps {
  initialData?: AlcoholData;
  supportMode?: boolean;
  onDataChange: (data: AlcoholData) => void;
}

const DRINK_TYPES = {
  'beer_pint': { name: 'Beer (Pint)', defaultAbv: 4.5, defaultAmount: 568 },
  'beer_bottle': { name: 'Beer (Bottle)', defaultAbv: 4.5, defaultAmount: 330 },
  'wine_glass': { name: 'Wine (Small Glass)', defaultAbv: 12, defaultAmount: 125 },
  'wine_large': { name: 'Wine (Large Glass)', defaultAbv: 12, defaultAmount: 175 },
  'wine_bottle': { name: 'Wine (Bottle)', defaultAbv: 12, defaultAmount: 750 },
  'spirits_single': { name: 'Spirits (Single)', defaultAbv: 40, defaultAmount: 25 },
  'spirits_double': { name: 'Spirits (Double)', defaultAbv: 40, defaultAmount: 50 },
  'cocktail': { name: 'Cocktail', defaultAbv: 15, defaultAmount: 150 },
  'champagne': { name: 'Champagne (Glass)', defaultAbv: 12, defaultAmount: 125 },
  'custom': { name: 'Custom Drink', defaultAbv: 0, defaultAmount: 0 }
};

const REDUCTION_TIPS = [
  "Start with a non-alcoholic drink to slow down consumption",
  "Alternate alcoholic drinks with water or soft drinks",
  "Make alcohol harder to access - don't keep it visible",
  "Plan activities that don't revolve around drinking",
  "Connect with friends who support your goals",
  "Use the delay technique - wait 10 minutes before drinking",
  "Keep a 'why I'm reducing' note on your phone",
  "Set a daily unit limit and track your progress",
  "Reward yourself for green days with something special",
  "Practice saying 'no thanks, I'm good' confidently"
];

const COPING_STRATEGIES = [
  "Deep breathing exercises",
  "Call a supportive friend",
  "Go for a walk",
  "Drink a large glass of water",
  "Do 10 jumping jacks",
  "Practice mindfulness for 5 minutes",
  "Write in a journal",
  "Listen to uplifting music",
  "Take a hot shower",
  "Engage in a hobby"
];

// Calculate alcohol units: (ABV × volume in ml) ÷ 1000
const calculateUnits = (abv: number, volumeMl: number): number => {
  return (abv * volumeMl) / 1000;
};

const getRiskLevel = (totalUnits: number): 'green' | 'amber' | 'red' => {
  if (totalUnits === 0) return 'green';
  if (totalUnits <= 2) return 'green';  // Low risk
  if (totalUnits <= 4) return 'amber';  // Moderate risk  
  return 'red'; // High risk
};

const getRiskMessage = (level: 'green' | 'amber' | 'red', units: number) => {
  switch (level) {
    case 'green':
      return units === 0 
        ? "🌟 Alcohol-free day! Your body and mind are thanking you."
        : "✅ Low-risk consumption. You're staying within healthy limits.";
    case 'amber':
      return "⚠️ Moderate risk. Consider slowing down or switching to non-alcoholic options.";
    case 'red':
      return "🚨 High-risk level. This exceeds daily guidelines. Please prioritise your wellbeing.";
    default:
      return "";
  }
};

export default function AlcoholTracker({ initialData, supportMode = false, onDataChange }: AlcoholTrackerProps) {
  const [data, setData] = useState<AlcoholData>(
    initialData || {
      units_consumed: 0,
      drinks: [],
      risk_level: 'green',
      support_mode: supportMode
    }
  );
  const [selectedDrinkType, setSelectedDrinkType] = useState<string>('');
  const [customAmount, setCustomAmount] = useState<number>(0);
  const [customAbv, setCustomAbv] = useState<number>(0);
  const [showAddDrink, setShowAddDrink] = useState(false);
  const [delayTimer, setDelayTimer] = useState<number | null>(null);
  const [selectedCoping, setSelectedCoping] = useState<string[]>([]);
  const [historicalData, setHistoricalData] = useState<Record<string, 'green' | 'amber' | 'red'>>({});
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [previousDayUnits, setPreviousDayUnits] = useState(0);
  const [costPerUnit, setCostPerUnit] = useState(3.50); // Average cost per unit
  const [showReduction, setShowReduction] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Calculate total units and risk level
    const totalUnits = data.drinks.reduce((sum, drink) => sum + drink.units, 0);
    const riskLevel = getRiskLevel(totalUnits);
    
    const updatedData = {
      ...data,
      units_consumed: totalUnits,
      risk_level: riskLevel
    };
    
    setData(updatedData);
    onDataChange(updatedData);
  }, [data.drinks]);

  // Load historical data when component mounts
  useEffect(() => {
    loadHistoricalData();
  }, [calendarMonth]);

  const loadHistoricalData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const startDate = format(startOfMonth(calendarMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(calendarMonth), 'yyyy-MM-dd');

      const { data: histData, error } = await supabase
        .from('daily_checkins')
        .select('date, cravings_level')
        .eq('user_id', user.id)
        .gte('date', startDate)
        .lte('date', endDate);

      if (error) throw error;

      const historical: Record<string, 'green' | 'amber' | 'red'> = {};
      
      (histData || []).forEach(entry => {
        const units = entry.cravings_level || 0;
        historical[entry.date] = getRiskLevel(units);
      });

      setHistoricalData(historical);
      
      // Load previous day data for reduction counter
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = format(yesterday, 'yyyy-MM-dd');
      
      const { data: prevData } = await supabase
        .from('daily_checkins')
        .select('cravings_level')
        .eq('user_id', user.id)
        .eq('date', yesterdayStr)
        .maybeSingle();

      if (prevData) {
        setPreviousDayUnits(prevData.cravings_level || 0);
      }
    } catch (error) {
      console.error('Error loading historical data:', error);
      toast({
        title: "Could not load history",
        description: "Using current session data only",
      });
    }
  };

  const getDayModifiers = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const riskLevel = historicalData[dateStr];
    
    return {
      'day-green': riskLevel === 'green',
      'day-amber': riskLevel === 'amber', 
      'day-red': riskLevel === 'red'
    };
  };

  const addDrink = () => {
    if (!selectedDrinkType) return;

    const drinkInfo = DRINK_TYPES[selectedDrinkType as keyof typeof DRINK_TYPES];
    const amount = selectedDrinkType === 'custom' ? customAmount : drinkInfo.defaultAmount;
    const abv = selectedDrinkType === 'custom' ? customAbv : drinkInfo.defaultAbv;
    
    // Validate custom drink inputs
    if (selectedDrinkType === 'custom' && (customAmount <= 0 || customAbv <= 0)) {
      toast({
        title: "Invalid custom drink",
        description: "Please enter valid amount (ml) and ABV %",
        variant: "destructive"
      });
      return;
    }
    
    const units = calculateUnits(abv, amount);

    const newDrink = {
      type: drinkInfo.name,
      amount,
      abv,
      units: Number(units.toFixed(1))
    };

    setData(prev => ({
      ...prev,
      drinks: [...prev.drinks, newDrink]
    }));

    // Reset form
    setSelectedDrinkType('');
    setCustomAmount(0);
    setCustomAbv(0);
    setShowAddDrink(false);
    
    toast({
      title: "Drink logged",
      description: `Added ${newDrink.type} (${units.toFixed(1)} units)`,
      action: data.drinks.length > 0 ? (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => removeDrink(data.drinks.length - 1)}
          className="ml-2"
        >
          <Minus className="w-3 h-3 mr-1" />
          Undo
        </Button>
      ) : undefined
    });
  };

  const removeDrink = (index: number) => {
    setData(prev => ({
      ...prev,
      drinks: prev.drinks.filter((_, i) => i !== index)
    }));
  };

  const startDelayTimer = (minutes: number) => {
    setDelayTimer(minutes);
    toast({
      title: `${minutes} minute delay started`,
      description: "Use this time to try a coping strategy. You're building new pathways!"
    });

    setTimeout(() => {
      setDelayTimer(null);
      toast({
        title: "Delay complete",
        description: "How do you feel now? Every pause strengthens your ability to choose."
      });
    }, minutes * 60 * 1000);
  };

  const logCravingResisted = () => {
    setData(prev => ({ ...prev, craving_resisted: true }));
    toast({
      title: "Craving resisted! 🌟",
      description: "You proved today that cravings don't control you. That's real strength."
    });
  };

  const getRiskColor = (level: 'green' | 'amber' | 'red') => {
    switch (level) {
      case 'green': return 'bg-accent';
      case 'amber': return 'bg-primary';
      case 'red': return 'bg-destructive';
      default: return 'bg-muted';
    }
  };

  const getDynamicBgColor = () => {
    switch (data.risk_level) {
      case 'green': return 'bg-accent';
      case 'amber': return 'bg-primary';
      case 'red': return 'bg-destructive';
      default: return 'bg-muted';
    }
  };

  return (
    <div className="space-y-6">
      {/* Reduction Counter */}
      {showReduction && (
        <ReductionCounter
          habitName="Alcohol"
          habitIcon={<Wine className="w-8 h-8" />}
          currentAmount={data.units_consumed}
          previousAmount={previousDayUnits}
          unit="units"
          costPerUnit={costPerUnit}
          currency="USD"
          onCostUpdate={setCostPerUnit}
        />
      )}
      {/* Quick Actions */}
      <div className="flex gap-2 mb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowReduction(!showReduction)}
          className="flex items-center gap-2"
        >
          <DollarSign className="w-4 h-4" />
          {showReduction ? 'Hide' : 'Show'} Savings
        </Button>
      </div>

      {/* Current Status */}
      <Card className="p-6">
        <div className="text-center mb-6">
          <div className={`w-16 h-16 ${getDynamicBgColor()} rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-500 ${
            data.units_consumed === 0 ? 'animate-success-glow' : ''
          }`}>
            <Wine className={`w-8 h-8 transition-all duration-500 ${
              data.units_consumed === 0 ? 'text-accent-foreground animate-sparkle' : 'text-primary-foreground'
            }`} />
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <h3 className="text-2xl font-bold text-foreground">
              {data.units_consumed.toFixed(1)} units consumed today
            </h3>
            <SparkleGuide
              title="How to track alcohol"
              items={[
                { title: 'Log each drink', description: 'Track type, amount, and ABV for accurate unit calculations.' },
                { title: 'Green/Amber/Red days', description: 'See at a glance how your consumption compares to health guidelines.' },
                { title: 'Track cravings', description: 'Log urges and what helped you resist to find your patterns.' },
                { title: 'View calendar', description: 'See your progress over time and celebrate streaks of green days.' }
              ]}
            />
          </div>
          <Badge 
            className={`text-sm px-4 py-1 ${
              data.risk_level === 'green' ? 'bg-accent text-accent-foreground' :
              data.risk_level === 'amber' ? 'bg-primary text-primary-foreground' :
              'bg-destructive text-destructive-foreground'
            }`}
          >
            {data.risk_level.toUpperCase()} DAY - {data.units_consumed.toFixed(1)} units
          </Badge>
          <p className="text-sm text-muted-foreground mt-2">
            {getRiskMessage(data.risk_level, data.units_consumed)}
          </p>
        </div>

        {/* Weekly Progress (placeholder for now) */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Weekly Green Days Goal</span>
            <span>5/7 days</span>
          </div>
          <Progress value={71} className="h-2" />
        </div>
      </Card>

      {/* Progress Calendar */}
      <Card className="p-6 bg-gradient-therapy border border-soft-accent/30">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-primary flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            Monthly Progress Calendar
          </h4>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCalendar(!showCalendar)}
          >
            {showCalendar ? 'Hide' : 'Show'} Calendar
          </Button>
        </div>
        
        {showCalendar && (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-accent"></div>
                <span className="text-foreground">Green Days (Low Risk)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-primary"></div>
                <span className="text-foreground">Amber Days (Moderate)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-destructive"></div>
                <span className="text-foreground">Red Days (High Risk)</span>
              </div>
            </div>
            
            <div className="calendar-container bg-background rounded-lg p-4">
              <Calendar
                mode="single"
                month={calendarMonth}
                onMonthChange={setCalendarMonth}
                className="w-full mx-auto pointer-events-auto"
                components={{
                  DayContent: ({ date }) => {
                    const isToday = isSameDay(date, new Date());
                    const dateStr = format(date, 'yyyy-MM-dd');
                    const riskLevel = historicalData[dateStr];
                    
                    return (
                      <div className={`
                        w-8 h-8 flex items-center justify-center text-sm font-medium rounded-md
                        transition-all duration-200 cursor-pointer
                        ${isToday ? 'ring-2 ring-primary ring-offset-1 ring-offset-background' : ''}
                        ${riskLevel === 'green' ? 'bg-accent text-accent-foreground shadow-sm' : ''}
                        ${riskLevel === 'amber' ? 'bg-primary text-white shadow-sm' : ''}
                        ${riskLevel === 'red' ? 'bg-destructive text-white shadow-sm' : ''}
                        ${!riskLevel ? 'hover:bg-muted text-muted-foreground' : ''}
                      `}>
                        {format(date, 'd')}
                      </div>
                    );
                  }
                }}
              />
            </div>
            
            <div className="text-center text-xs text-muted-foreground mt-4">
              🎯 Aim for more green days each month. Every healthy choice counts!
            </div>
          </div>
        )}
      </Card>

      {/* Support Mode Features */}
      {supportMode && (
        <Card className="p-6 border-primary/30 bg-gradient-therapy">
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-accent" />
            Support Tools
          </h4>
          
          {/* Craving Tracking */}
          <div className="space-y-4 mb-6">
            <div>
              <Label className="text-sm font-medium">Craving strength today (0-10)</Label>
              <Slider
                value={[data.craving_strength || 0]}
                onValueChange={(value) => setData(prev => ({ ...prev, craving_strength: value[0] }))}
                max={10}
                step={1}
                className="mt-2"
              />
              <div className="text-center text-sm text-muted-foreground mt-1">
                {data.craving_strength || 0}/10
              </div>
            </div>

            {/* Delay Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => startDelayTimer(5)}
                disabled={delayTimer !== null}
                className="flex items-center gap-1"
              >
                <Clock className="w-4 h-4" />
                5 min delay
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => startDelayTimer(10)}
                disabled={delayTimer !== null}
                className="flex items-center gap-1"
              >
                <Clock className="w-4 h-4" />
                10 min delay
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={logCravingResisted}
                className="flex items-center gap-1 text-accent border-accent/30"
              >
                <CheckCircle className="w-4 h-4" />
                Resisted urge
              </Button>
            </div>

            {delayTimer && (
              <div className="p-3 bg-secondary/20 rounded-lg border border-secondary/30">
                <p className="text-sm text-foreground">
                  ⏰ Delay timer active: {delayTimer} minutes. Try a coping strategy below!
                </p>
              </div>
            )}
          </div>

          {/* Coping Strategies */}
          <div className="mb-4">
            <Label className="text-sm font-medium mb-2 block">Coping strategies used today:</Label>
            <div className="grid grid-cols-2 gap-2">
              {COPING_STRATEGIES.map((strategy) => (
                <div key={strategy} className="flex items-center space-x-2">
                  <Checkbox
                    id={strategy}
                    checked={selectedCoping.includes(strategy)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedCoping([...selectedCoping, strategy]);
                      } else {
                        setSelectedCoping(selectedCoping.filter(s => s !== strategy));
                      }
                    }}
                  />
                  <Label htmlFor={strategy} className="text-xs cursor-pointer">
                    {strategy}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Triggers */}
          <div>
            <Label className="text-sm font-medium mb-2 block">What triggered cravings today?</Label>
            <Textarea
              placeholder="Work stress, social situation, boredom..."
              value={data.triggers || ''}
              onChange={(e) => setData(prev => ({ ...prev, triggers: e.target.value }))}
              className="text-sm"
            />
          </div>
        </Card>
      )}

      {/* Add Drinks */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold">Today's Drinks</h4>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddDrink(!showAddDrink)}
          >
            Add Drink
          </Button>
        </div>

        {showAddDrink && (
          <div className="space-y-4 mb-6 p-4 bg-muted/50 rounded-lg">
            <div>
              <Label className="text-sm font-medium">Drink Type</Label>
              <Select value={selectedDrinkType} onValueChange={setSelectedDrinkType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select drink type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DRINK_TYPES).map(([key, drink]) => (
                    <SelectItem key={key} value={key}>
                      {drink.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedDrinkType === 'custom' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Amount (ml)</Label>
                  <Input
                    type="number"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(Number(e.target.value))}
                    placeholder="330"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">ABV %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={customAbv}
                    onChange={(e) => setCustomAbv(Number(e.target.value))}
                    placeholder="4.5"
                  />
                </div>
              </div>
            )}

            <Button 
              onClick={addDrink} 
              disabled={
                !selectedDrinkType || 
                (selectedDrinkType === 'custom' && (customAmount <= 0 || customAbv <= 0))
              }
            >
              Add to Today's Total
            </Button>
          </div>
        )}

        {/* Drinks List */}
        {data.drinks.length > 0 ? (
          <div className="space-y-2">
            {data.drinks.map((drink, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-background rounded-lg border">
                <div>
                  <p className="font-medium text-sm">{drink.type}</p>
                  <p className="text-xs text-muted-foreground">
                    {drink.amount}ml, {drink.abv}% ABV
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="secondary">{drink.units} units</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeDrink(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">
            No drinks logged today. Keep up the great work! 🌟
          </p>
        )}
      </Card>

      {/* Reduction Tips */}
      <Card className="p-6">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <TrendingDown className="w-5 h-5 text-accent" />
          Daily Reduction Tip
        </h4>
        <div className="p-4 bg-gradient-therapy rounded-lg border border-accent/30">
          <p className="text-sm text-foreground">
            💡 {REDUCTION_TIPS[Math.floor(Math.random() * REDUCTION_TIPS.length)]}
          </p>
        </div>
      </Card>
    </div>
  );
}